// Monkey Ride 3D - Core Game Engine using Babylon.js
import * as BABYLON from '@babylonjs/core';

export const LANE_POSITIONS = [-3, 0, 3]; // left, center, right
export const LANE_WIDTH = 3;
export const ROAD_SPEED_INITIAL = 8;
export const ROAD_SPEED_MAX = 25;
export const ROAD_SPEED_INCREMENT = 0.002;

export const ENVIRONMENTS = {
  jungle: {
    name: 'Jungle Road',
    skyColor: new BABYLON.Color4(0.1, 0.4, 0.1, 1),
    fogColor: new BABYLON.Color3(0.2, 0.5, 0.2),
    roadColor: '#4a7c59',
    ambientColor: new BABYLON.Color3(0.2, 0.4, 0.1),
    fogDensity: 0.02,
  },
  city: {
    name: 'City Street',
    skyColor: new BABYLON.Color4(0.1, 0.1, 0.3, 1),
    fogColor: new BABYLON.Color3(0.2, 0.2, 0.4),
    roadColor: '#333344',
    ambientColor: new BABYLON.Color3(0.1, 0.1, 0.3),
    fogDensity: 0.015,
  },
  beach: {
    name: 'Beach Highway',
    skyColor: new BABYLON.Color4(0.4, 0.7, 1.0, 1),
    fogColor: new BABYLON.Color3(0.6, 0.8, 1.0),
    roadColor: '#c2a96e',
    ambientColor: new BABYLON.Color3(0.4, 0.6, 0.8),
    fogDensity: 0.01,
  },
  snow: {
    name: 'Snow Mountain',
    skyColor: new BABYLON.Color4(0.7, 0.8, 1.0, 1),
    fogColor: new BABYLON.Color3(0.8, 0.9, 1.0),
    roadColor: '#e8f0f8',
    ambientColor: new BABYLON.Color3(0.6, 0.7, 0.9),
    fogDensity: 0.025,
  },
  space: {
    name: 'Space Highway',
    skyColor: new BABYLON.Color4(0.0, 0.0, 0.05, 1),
    fogColor: new BABYLON.Color3(0.05, 0.0, 0.15),
    roadColor: '#1a0a3a',
    ambientColor: new BABYLON.Color3(0.1, 0.0, 0.3),
    fogDensity: 0.008,
  },
};

export const CHARACTERS = [
  { id: 'classic', name: 'Classic Monkey', color: '#8B4513', price: 0, unlocked: true, emoji: '🐒' },
  { id: 'ninja', name: 'Ninja Monkey', color: '#1a1a2e', price: 500, unlocked: false, emoji: '🥷' },
  { id: 'pirate', name: 'Pirate Monkey', color: '#8B0000', price: 800, unlocked: false, emoji: '🏴‍☠️' },
  { id: 'golden', name: 'Golden Monkey', color: '#FFD700', price: 1500, unlocked: false, emoji: '✨' },
  { id: 'space', name: 'Space Monkey', color: '#4169E1', price: 2000, unlocked: false, emoji: '🚀' },
];

export const VEHICLES = [
  { id: 'scooter', name: 'Scooter', color: '#FF6B6B', price: 0, unlocked: true, emoji: '🛵', speedBonus: 0 },
  { id: 'skateboard', name: 'Skateboard', color: '#4ECDC4', price: 300, unlocked: false, emoji: '🛹', speedBonus: 0.5 },
  { id: 'hoverboard', name: 'Hoverboard', color: '#45B7D1', price: 600, unlocked: false, emoji: '🛸', speedBonus: 1.0 },
  { id: 'jeep', name: 'Jungle Jeep', color: '#2ECC71', price: 1000, unlocked: false, emoji: '🚙', speedBonus: 0.5 },
  { id: 'rocket', name: 'Rocket Bike', color: '#E74C3C', price: 2500, unlocked: false, emoji: '🚀', speedBonus: 2.0 },
];

export const POWER_UPS = [
  { id: 'magnet', name: 'Magnet', duration: 8000, color: '#FF6B9D', emoji: '🧲', description: 'Attracts all coins' },
  { id: 'shield', name: 'Shield', duration: 6000, color: '#4FC3F7', emoji: '🛡️', description: 'Blocks one crash' },
  { id: 'multiplier', name: 'Coin Multiplier', duration: 10000, color: '#FFD700', emoji: '✖️', description: 'Doubles coins' },
  { id: 'jetpack', name: 'Jetpack', duration: 5000, color: '#FF9800', emoji: '🚀', description: 'Fly temporarily' },
  { id: 'speedboost', name: 'Speed Boost', duration: 4000, color: '#E91E63', emoji: '⚡', description: 'Temporary speed' },
];

export const OBSTACLES = [
  { id: 'barrier', name: 'Barrier', height: 1.2, width: 2.5, color: '#FF4444', canSlide: false, canJump: true },
  { id: 'lowbar', name: 'Low Bar', height: 0.6, width: 2.5, color: '#FF8800', canSlide: true, canJump: false },
  { id: 'train', name: 'Train', height: 2.0, width: 2.5, color: '#666688', canSlide: false, canJump: false },
  { id: 'crate', name: 'Crate', height: 1.0, width: 1.0, color: '#8B6914', canSlide: false, canJump: true },
  { id: 'fire', name: 'Fire', height: 1.5, width: 1.0, color: '#FF6600', canSlide: false, canJump: true },
];

export function createScene(canvas, environmentKey = 'jungle') {
  const engine = new BABYLON.Engine(canvas, true, {
    preserveDrawingBuffer: true,
    stencil: true,
    adaptToDeviceRatio: true,
  });

  const scene = new BABYLON.Scene(engine);
  const env = ENVIRONMENTS?.[environmentKey] || ENVIRONMENTS?.jungle;

  scene.clearColor = env.skyColor;
  scene.fogMode = BABYLON?.Scene?.FOGMODE_EXP2;
  scene.fogColor = env.fogColor;
  scene.fogDensity = env.fogDensity;
  scene.ambientColor = env.ambientColor;

  return { engine, scene };
}

export function createCamera(scene) {
  const camera = new BABYLON.FollowCamera('camera', new BABYLON.Vector3(0, 8, -14), scene);
  camera.radius = 14;
  camera.heightOffset = 8;
  camera.rotationOffset = 0;
  camera.cameraAcceleration = 0.05;
  camera.maxCameraSpeed = 20;
  return camera;
}

export function createLighting(scene) {
  const hemi = new BABYLON.HemisphericLight('hemi', new BABYLON.Vector3(0, 1, 0), scene);
  hemi.intensity = 0.7;
  hemi.diffuse = new BABYLON.Color3(1, 0.95, 0.8);
  hemi.specular = new BABYLON.Color3(0.3, 0.3, 0.3);

  const dir = new BABYLON.DirectionalLight('dir', new BABYLON.Vector3(-1, -2, 1), scene);
  dir.intensity = 0.8;
  dir.diffuse = new BABYLON.Color3(1, 0.9, 0.7);

  const shadowGen = new BABYLON.ShadowGenerator(512, dir);
  shadowGen.useBlurExponentialShadowMap = true;
  shadowGen.blurKernel = 16;

  return { hemi, dir, shadowGen };
}

export function createRoadSegment(scene, z, environmentKey = 'jungle') {
  const road = BABYLON?.MeshBuilder?.CreateBox('road', { width: 12, height: 0.2, depth: 20 }, scene);
  road.position = new BABYLON.Vector3(0, -0.1, z);

  const mat = new BABYLON.StandardMaterial('roadMat', scene);
  const env = ENVIRONMENTS?.[environmentKey] || ENVIRONMENTS?.jungle;
  mat.diffuseColor = BABYLON?.Color3?.FromHexString(env.roadColor);
  mat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
  road.material = mat;
  road.receiveShadows = true;

  // Lane dividers
  for (let i = -1; i <= 1; i += 2) {
    const divider = BABYLON?.MeshBuilder?.CreateBox('divider', { width: 0.15, height: 0.05, depth: 20 }, scene);
    divider.position = new BABYLON.Vector3(i * LANE_WIDTH / 2 + i * LANE_WIDTH / 2, 0.05, z);
    const dMat = new BABYLON.StandardMaterial('divMat', scene);
    dMat.diffuseColor = new BABYLON.Color3(1, 1, 0.5);
    dMat.emissiveColor = new BABYLON.Color3(0.3, 0.3, 0.1);
    divider.material = dMat;
    divider.parent = road;
  }

  return road;
}

export function createPlayerMesh(scene, characterId = 'classic') {
  const char = CHARACTERS?.find(c => c?.id === characterId) || CHARACTERS?.[0];

  const root = new BABYLON.TransformNode('playerRoot', scene);

  // Body
  const body = BABYLON?.MeshBuilder?.CreateCylinder('body', { height: 1.2, diameter: 0.8, tessellation: 8 }, scene);
  body.position = new BABYLON.Vector3(0, 0.6, 0);
  body.parent = root;

  // Head
  const head = BABYLON?.MeshBuilder?.CreateSphere('head', { diameter: 0.7, segments: 8 }, scene);
  head.position = new BABYLON.Vector3(0, 1.5, 0);
  head.parent = root;

  // Vehicle base
  const vehicle = BABYLON?.MeshBuilder?.CreateBox('vehicle', { width: 1.8, height: 0.4, depth: 2.5 }, scene);
  vehicle.position = new BABYLON.Vector3(0, 0.2, 0);
  vehicle.parent = root;

  // Wheels
  for (const [wx, wz] of [[-0.9, -0.8], [0.9, -0.8], [-0.9, 0.8], [0.9, 0.8]]) {
    const wheel = BABYLON?.MeshBuilder?.CreateCylinder('wheel', { height: 0.2, diameter: 0.5, tessellation: 12 }, scene);
    wheel.rotation.z = Math.PI / 2;
    wheel.position = new BABYLON.Vector3(wx, 0, wz);
    wheel.parent = root;
    const wMat = new BABYLON.StandardMaterial('wMat', scene);
    wMat.diffuseColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    wheel.material = wMat;
  }

  const mat = new BABYLON.StandardMaterial('playerMat', scene);
  mat.diffuseColor = BABYLON?.Color3?.FromHexString(char?.color);
  mat.specularColor = new BABYLON.Color3(0.5, 0.5, 0.5);
  mat.specularPower = 32;
  body.material = mat;
  head.material = mat;

  const vMat = new BABYLON.StandardMaterial('vMat', scene);
  vMat.diffuseColor = new BABYLON.Color3(0.8, 0.2, 0.2);
  vehicle.material = vMat;

  root.position = new BABYLON.Vector3(0, 0, 0);

  return root;
}

export function createCoinMesh(scene, position) {
  const coin = BABYLON?.MeshBuilder?.CreateCylinder('coin', { height: 0.15, diameter: 0.6, tessellation: 16 }, scene);
  coin.position = position?.clone();
  coin.position.y = 1.0;

  const mat = new BABYLON.StandardMaterial('coinMat', scene);
  mat.diffuseColor = new BABYLON.Color3(1, 0.85, 0);
  mat.emissiveColor = new BABYLON.Color3(0.5, 0.4, 0);
  mat.specularColor = new BABYLON.Color3(1, 1, 0.5);
  mat.specularPower = 64;
  coin.material = mat;

  return coin;
}

export function createObstacleMesh(scene, position, obstacleType = 'barrier') {
  const obs = OBSTACLES?.find(o => o?.id === obstacleType) || OBSTACLES?.[0];
  const mesh = BABYLON?.MeshBuilder?.CreateBox('obstacle_' + obstacleType, {
    width: obs?.width,
    height: obs?.height,
    depth: 1.5,
  }, scene);
  mesh.position = position?.clone();
  mesh.position.y = obs?.height / 2;

  const mat = new BABYLON.StandardMaterial('obsMat', scene);
  mat.diffuseColor = BABYLON?.Color3?.FromHexString(obs?.color);
  mat.emissiveColor = BABYLON?.Color3?.FromHexString(obs?.color)?.scale(0.2);
  mesh.material = mat;

  return mesh;
}

export function createPowerUpMesh(scene, position, powerUpId = 'magnet') {
  const pu = POWER_UPS?.find(p => p?.id === powerUpId) || POWER_UPS?.[0];

  const mesh = BABYLON?.MeshBuilder?.CreateSphere('powerup_' + powerUpId, { diameter: 0.8, segments: 8 }, scene);
  mesh.position = position?.clone();
  mesh.position.y = 1.2;

  const mat = new BABYLON.StandardMaterial('puMat', scene);
  mat.diffuseColor = BABYLON?.Color3?.FromHexString(pu?.color);
  mat.emissiveColor = BABYLON?.Color3?.FromHexString(pu?.color)?.scale(0.4);
  mat.alpha = 0.9;
  mesh.material = mat;

  return mesh;
}

export function createParticleSystem(scene, emitter, color1, color2) {
  const ps = new BABYLON.ParticleSystem('particles', 50, scene);
  ps.particleTexture = new BABYLON.Texture('https://assets.babylonjs.com/textures/flare.png', scene);
  ps.emitter = emitter;
  ps.minEmitBox = new BABYLON.Vector3(-0.3, 0, -0.3);
  ps.maxEmitBox = new BABYLON.Vector3(0.3, 0, 0.3);
  ps.color1 = color1;
  ps.color2 = color2;
  ps.colorDead = new BABYLON.Color4(0, 0, 0, 0);
  ps.minSize = 0.1;
  ps.maxSize = 0.4;
  ps.minLifeTime = 0.2;
  ps.maxLifeTime = 0.6;
  ps.emitRate = 100;
  ps.blendMode = BABYLON?.ParticleSystem?.BLENDMODE_ONEONE;
  ps.gravity = new BABYLON.Vector3(0, 5, 0);
  ps.direction1 = new BABYLON.Vector3(-2, 4, -2);
  ps.direction2 = new BABYLON.Vector3(2, 4, 2);
  ps.minAngularSpeed = 0;
  ps.maxAngularSpeed = Math.PI;
  ps.minEmitPower = 1;
  ps.maxEmitPower = 3;
  ps.updateSpeed = 0.02;
  return ps;
}

export default { createScene, createCamera, createLighting, createRoadSegment, createPlayerMesh, createCoinMesh, createObstacleMesh, createPowerUpMesh, createParticleSystem, LANE_POSITIONS, ENVIRONMENTS, CHARACTERS, VEHICLES, POWER_UPS, OBSTACLES };
